/**
 * run-with-usl.auth-latches.ts
 *
 * Module-level boolean latches that coordinate auth-error handling across
 * run-with-usl.tsx, state/session/actions.tsx, and usl-login-form.tsx.
 *
 * This module is intentionally kept free of any imports so it can be safely
 * imported by any module without risk of circular dependencies or premature
 * side-effects (e.g. evaluating run-with-usl.tsx before the Redux store exists).
 */

/*
 * Session-timeout latch
 *
 * Guards against multiple 401 auth errors firing in quick succession during a session timeout.
 * When a session expires, multiple in-flight requests can all fail with 401 simultaneously.
 * The first 401 hits the sessionTimeout branch in the AuthEvents subscriber and dispatches logout.
 * Subsequent 401s would otherwise fall through to the else branch and call setUnexpectedAuthError(true),
 * causing the TroubleshootingPanel to appear alongside the SessionInvalidationAlertModal.
 *
 * The latch lives at module scope so that resetSessionTimeoutLatch() can be called from
 * usl-login-form.tsx when the user clicks "Log in". This is necessary because
 * registerAndAuthorize() can fail before navigating away (e.g. network error on the /register
 * endpoint). In that case the browser stays on the same page and the AuthEvents subscriber fires
 * again — but with the latch still true, the error would be silently swallowed. Resetting the
 * latch when the user initiates a new login attempt ensures those errors surface correctly.
 */
let isSessionTimeoutLogoutInProgress = false;

/**
 * Returns the current value of the session-timeout latch.
 * Used by the AuthEvents subscriber in run-with-usl.tsx to decide whether to suppress auth errors.
 */
export const getSessionTimeoutLatch = (): boolean =>
  isSessionTimeoutLogoutInProgress;

/**
 * Sets the session-timeout latch.
 * Call with `true` when a session-timeout 401 is detected, and with `false`
 * after the safety-net timeout expires.
 */
export const setSessionTimeoutLatch = (value: boolean): void => {
  isSessionTimeoutLogoutInProgress = value;
};

/**
 * Resets the session-timeout latch to false.
 * Call this at the start of handleButtonClick in usl-login-form.tsx, before
 * AuthClient.registerAndAuthorize() is invoked, so that auth errors from a new login attempt
 * are not silently suppressed by a stale latch left over from a previous session-timeout event.
 */
export const resetSessionTimeoutLatch = (): void => {
  isSessionTimeoutLogoutInProgress = false;
};

/*
 * Auth-error suppression latch
 *
 * Suppresses auth:error events that are emitted by AuthClient.signOut() during a user-initiated
 * logout, so they are not misinterpreted as unexpected auth errors and shown as the
 * TroubleshootingPanel to a user who just clicked "Log out".
 *
 * When a hard logout is initiated, AuthClient.signOut() calls the /logout endpoint and then
 * performs a top-level redirect. If the /logout endpoint fails (e.g. network error, 500), the
 * AuthEvents subscriber may fire an auth:error event before the redirect occurs. Without this
 * guard, that error would fall through to the else branch and call setUnexpectedAuthError(true),
 * showing the TroubleshootingPanel during what should be a clean logout.
 *
 * The latch is set to true in state/session/actions.tsx immediately before AuthClient.signOut()
 * is called. For the normal logout path the page navigates away and the module is re-initialised,
 * so the latch never needs to be reset. For any soft-logout path where the page does NOT navigate
 * away, the latch should be reset to false at the start of the next login attempt via
 * setSuppressAuthErrors(false) in usl-login-form.tsx.
 */
let suppressAuthErrors = false;

/**
 * Returns the current value of the auth-error suppression latch.
 * Used by the AuthEvents subscriber in run-with-usl.tsx to decide whether to suppress auth errors.
 */
export const getSuppressAuthErrors = (): boolean => suppressAuthErrors;

/**
 * Controls whether auth:error events emitted by AuthClient.signOut() are suppressed.
 *
 * Call with `true` in state/session/actions.tsx immediately before AuthClient.signOut() is invoked,
 * to prevent logout-related auth errors from showing the TroubleshootingPanel.
 *
 * Call with `false` in usl-login-form.tsx at the start of handleButtonClick (alongside
 * resetSessionTimeoutLatch()), before AuthClient.registerAndAuthorize() is invoked, to ensure
 * that auth errors from a subsequent login attempt are not silently suppressed. This is only
 * needed for the stayOnTheSamePage soft-logout path where the page does not navigate away.
 */
export const setSuppressAuthErrors = (value: boolean): void => {
  suppressAuthErrors = value;
};
